import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth } from "../firebase/firebaseConfig";
import { sendPasswordResetEmail } from "firebase/auth";
import AuthLayout from "./ui/AuthLayout";
import Field from "./ui/Field";
import Button from "./ui/Button";

// Maps Firebase error codes to friendly, user-facing copy.
// Note: for privacy (and because the project may have email-enumeration
// protection enabled) we treat "user-not-found" as a success and show the
// same neutral "if an account exists" message regardless.
const ERROR_MESSAGES = {
  "auth/invalid-email": "That doesn't look like a valid email address.",
  "auth/missing-email": "Please enter your email address.",
  "auth/too-many-requests":
    "Too many attempts. Please wait a few minutes and try again.",
  "auth/network-request-failed":
    "Network error. Check your connection and try again.",
};

export default function ForgotPassword() {
  const [user, setUser] = useState({
    email: "",
  });
  // idle | loading | sent | error
  const [status, setStatus] = useState("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    if (auth?.currentUser) navigate("/");
  }, [navigate]);

  const onChangeHandler = (e) => {
    setUser((prevUser) => {
      return { ...prevUser, [e.target.name]: e.target.value };
    });
    if (status === "error") {
      setStatus("idle");
      setErrorMessage("");
    }
  };

  const submitHandler = (event) => {
    event.preventDefault();
    setStatus("loading");
    setErrorMessage("");

    // No actionCodeSettings: Firebase sends its standard, most-deliverable
    // password-reset email pointing to its hosted "set new password" page.
    sendPasswordResetEmail(auth, user.email)
      .then(() => {
        setStatus("sent");
      })
      .catch((error) => {
        // Don't reveal whether the address has an account.
        if (error?.code === "auth/user-not-found") {
          setStatus("sent");
          return;
        }
        console.error("password reset error", error?.code, error?.message);
        setErrorMessage(
          ERROR_MESSAGES[error?.code] ||
            "Something went wrong. Please try again."
        );
        setStatus("error");
      });
  };

  return (
    <AuthLayout
      title="Reset your password"
      subtitle={
        status === "sent"
          ? undefined
          : "Enter your email and we'll send you a link to reset your password."
      }
      footer={
        <a
          href="/login"
          className="font-medium text-accent transition-colors hover:text-accent-soft"
        >
          Back to login
        </a>
      }
    >
      {status === "sent" ? (
        <p className="text-center text-sm leading-relaxed text-white/70">
          If an account exists for that email, we've sent a reset link. Check
          your inbox (and spam folder) and follow the link to set a new
          password.
        </p>
      ) : (
        <form onSubmit={submitHandler} className="space-y-4">
          <Field
            label="Email"
            id="reset-email"
            hideLabel
            type="email"
            name="email"
            value={user.email}
            onChange={onChangeHandler}
            autoComplete="email"
            placeholder="Email"
            required
            error={status === "error" ? errorMessage : ""}
          />
          <Button
            type="submit"
            variant="primary"
            className="w-full"
            disabled={status === "loading"}
          >
            {status === "loading" ? "Sending…" : "Send reset link"}
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
